import { Modal } from '../Modal';
import type { NotaFiscalRow } from '../../modules/notas-fiscais/NotasFiscaisLista';

export interface ConsultaSefazData {
  chave: string;
  numero: number;
  serie: string;
  status_local: string;
  status_sefaz: 'autorizada' | 'cancelada' | 'denegada' | 'nao_consta' | 'desconhecido';
  cStat: string;
  xMotivo: string;
  protocolo: string | { nProt?: string; dhRecbto?: string; cStat?: string; xMotivo?: string } | null;
  eventos?: Array<{
    tpEvento?: string;
    cStat?: string;
    xMotivo?: string;
    nProt?: string;
    dhRegEvento?: string;
  }>;
}

interface Props {
  nota: NotaFiscalRow;
  data: ConsultaSefazData;
  tipoLabel: string;
  onClose: () => void;
}

const SEFAZ_LABEL: Record<string, string> = {
  autorizada: 'AUTORIZADA na SEFAZ',
  cancelada: 'CANCELADA na SEFAZ',
  denegada: 'DENEGADA na SEFAZ',
  nao_consta: 'NAO CONSTA na SEFAZ (nunca foi aceita)',
  desconhecido: 'Status nao reconhecido',
};

const SEFAZ_COR: Record<string, { bg: string; border: string; fg: string }> = {
  autorizada: { bg: '#dcfce7', border: '#86efac', fg: '#166534' },
  cancelada: { bg: '#fee2e2', border: '#fecaca', fg: '#991b1b' },
  denegada: { bg: '#fee2e2', border: '#fecaca', fg: '#991b1b' },
  nao_consta: { bg: '#fef9c3', border: '#fde68a', fg: '#854d0e' },
  desconhecido: { bg: 'var(--card-bg)', border: 'var(--border)', fg: 'var(--text)' },
};

export function ModalConsultaSefaz({ nota, data, tipoLabel, onClose }: Props) {
  const numFmt = String(data.numero).padStart(6, '0');
  const cor = SEFAZ_COR[data.status_sefaz] || SEFAZ_COR.desconhecido!;
  const sefazLabel = SEFAZ_LABEL[data.status_sefaz] || data.status_sefaz;

  const divergente =
    (data.status_local === 'autorizada' && data.status_sefaz !== 'autorizada' && data.status_sefaz !== 'cancelada') ||
    (data.status_local === 'rejeitada' && data.status_sefaz === 'autorizada') ||
    (data.status_local !== 'cancelada' && data.status_sefaz === 'cancelada');

  const protocolo = typeof data.protocolo === 'object' ? data.protocolo : null;

  return (
    <Modal
      aberto
      onFechar={onClose}
      titulo={`${tipoLabel} nº ${numFmt}/${data.serie} — Consulta SEFAZ`}
      largura={680}
      rodape={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} className="btn">Fechar</button>
        </div>
      }
    >
      <div style={{ display: 'grid', gap: 12, fontSize: 13 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 10 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Status local</div>
            <div style={{ fontWeight: 600, textTransform: 'uppercase' }}>{data.status_local}</div>
          </div>
          <div style={{ border: `1px solid ${cor.border}`, background: cor.bg, color: cor.fg, borderRadius: 6, padding: 10 }}>
            <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>Status na SEFAZ</div>
            <div style={{ fontWeight: 600, textTransform: 'uppercase' }}>{sefazLabel}</div>
          </div>
        </div>

        {divergente && (
          <div style={{ background: '#ffedd5', border: '1px solid #fdba74', color: '#9a3412', borderRadius: 6, padding: 10, fontWeight: 500 }}>
            ⚠ DIVERGENCIA — status local diferente do status na SEFAZ.
          </div>
        )}

        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 4, columnGap: 12 }}>
          <div style={{ color: 'var(--text-muted)' }}>cStat</div>
          <div style={{ fontFamily: 'monospace' }}>{data.cStat || '—'}</div>
          <div style={{ color: 'var(--text-muted)' }}>Motivo</div>
          <div>{data.xMotivo}</div>
          <div style={{ color: 'var(--text-muted)' }}>Chave</div>
          <div style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>{data.chave || nota.chave}</div>
        </div>

        {protocolo?.nProt && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase' }}>
              Protocolo de autorizacao
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 4, columnGap: 12 }}>
              <div style={{ color: 'var(--text-muted)' }}>nProt</div>
              <div style={{ fontFamily: 'monospace' }}>{protocolo.nProt}</div>
              {protocolo.dhRecbto && (
                <>
                  <div style={{ color: 'var(--text-muted)' }}>dhRecbto</div>
                  <div style={{ fontFamily: 'monospace', fontSize: 11 }}>{protocolo.dhRecbto}</div>
                </>
              )}
              {protocolo.cStat && (
                <>
                  <div style={{ color: 'var(--text-muted)' }}>cStat prot.</div>
                  <div style={{ fontFamily: 'monospace' }}>{protocolo.cStat} — {protocolo.xMotivo}</div>
                </>
              )}
            </div>
          </div>
        )}

        {data.eventos && data.eventos.length > 0 && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase' }}>
              Eventos registrados ({data.eventos.length})
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              {data.eventos.map((ev, i) => (
                <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 4, padding: 8, fontSize: 11, display: 'grid', gridTemplateColumns: '110px 1fr', rowGap: 2, columnGap: 12 }}>
                  <div style={{ color: 'var(--text-muted)' }}>tpEvento</div><div style={{ fontFamily: 'monospace' }}>{ev.tpEvento}</div>
                  <div style={{ color: 'var(--text-muted)' }}>cStat</div><div style={{ fontFamily: 'monospace' }}>{ev.cStat}</div>
                  <div style={{ color: 'var(--text-muted)' }}>Motivo</div><div>{ev.xMotivo}</div>
                  {ev.nProt && (<>
                    <div style={{ color: 'var(--text-muted)' }}>nProt</div>
                    <div style={{ fontFamily: 'monospace' }}>{ev.nProt}</div>
                  </>)}
                  {ev.dhRegEvento && (<>
                    <div style={{ color: 'var(--text-muted)' }}>dhRegEvento</div>
                    <div style={{ fontFamily: 'monospace' }}>{ev.dhRegEvento}</div>
                  </>)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
